<script lang="ts">
	import { getContext, createEventDispatcher } from 'svelte';
	import { Col, Input, Row, Select, Stack, Text, Toggle } from '@Shelly/shelly-design-system';
	import type { Writable } from 'svelte/store';
	import { format } from 'svelte-i18n';
	import { setPresenceMainConfig } from 'src/components/Radar/helpers/requests';

	const radarSetupContext: Writable<any> = getContext('radarSetupContext');
	const dispatch = createEventDispatcher();

	$: device = $radarSetupContext?.device;
	$: settings = $radarSetupContext?.settings ?? {};

	$: sensitivityOptions = [
		{ title: $format('presence_config_power_low', { default: 'Low' }), value: 'low' },
		{ title: $format('presence_config_power_medium', { default: 'Medium' }), value: 'medium' },
		{ title: $format('presence_config_power_high', { default: 'High' }), value: 'high' },
	];

	let sensorHeight: string = String(settings?.presence?.sensor?.height ?? 2);
	let sensitivity: string = settings?.presence?.sensor?.sensitivity ?? 'medium';
	let isFlipped: boolean = settings?.presence?.sensor?.flipped ?? false;

	$: selectedSensitivity = sensitivityOptions.find((o) => o.value === sensitivity);

	let saving = false;
	let saved = false;
	let saveError = false;

	async function save() {
		if (!device) return;
		saving = true;
		saveError = false;

		const result = await setPresenceMainConfig(device, {
			sensor: {
				height: Number(sensorHeight),
				sensitivity,
				flipped: isFlipped,
			},
		});

		saving = false;

		if (result?.isok) {
			saved = true;
			radarSetupContext.update((ctx) => ({ ...ctx, sensorConfigured: true }));
			setTimeout(() => dispatch('next'), 800);
		} else {
			saveError = true;
		}
	}

	function getSelectValue(e: CustomEvent | TouchEvent): string {
		if (typeof (e as CustomEvent).detail === 'string') return (e as CustomEvent).detail;
		if ((e as CustomEvent).detail?.value) return (e as CustomEvent).detail.value as string;
		return 'medium';
	}
</script>

<div class="sensor-step">
	<Text element="h2" class="mb-xs">
		{$format('presence_wizard_config_title', { default: 'Configure your sensor' })}
	</Text>
	<p class="step-subtitle mb-l">
		{$format('presence_wizard_config_subtitle', {
			default:
				'Tell the device how it is installed so it can calculate positions accurately. You can update these later in the sensor settings.',
		})}
	</p>

	<Stack gap="m">
		<Row>
			<Col>
				<Input
					type="number"
					min="0"
					max="5"
					step="0.1"
					bind:value={sensorHeight}
					label={$format('presence_config_sensor_height_label', {
						default: 'Sensor height (m)',
					})}
					id="wizard-sensor-height"
					aria-labelledby="wizard-sensor-height"
				/>
				<p class="field-hint">
					{$format('presence_wizard_config_height_hint', {
						default:
							'The height of the device above the floor. Typical ceiling mount: 2.4–3 m.',
					})}
				</p>
			</Col>
		</Row>

		<Row>
			<Col>
				<Select
					aria-labelledby="wizard-sensitivity"
					icon={null}
					options={sensitivityOptions}
					label={$format('presence_config_sensitivity_label', {
						default: 'Detection sensitivity',
					})}
					value={selectedSensitivity}
					on:change={(e) => (sensitivity = getSelectValue(e))}
					id="wizard-sensitivity"
				/>
				<p class="field-hint">
					{$format('presence_wizard_config_sensitivity_hint', {
						default:
							'High sensitivity picks up stationary presence. Low reduces false triggers in busy areas.',
					})}
				</p>
			</Col>
		</Row>

		<Row>
			<Col>
				<div class="toggle-row">
					<div class="toggle-labels">
						<p class="toggle-title">
							{$format('presence_config_flipped_label', { default: 'Flipped mounting' })}
						</p>
						<p class="field-hint">
							{$format('presence_config_flipped_tooltip', {
								default: 'Enable if the device is mounted upside down (rotated 180°).',
							})}
						</p>
					</div>
					<Toggle
						aria-label={$format('toggle_flipped_mounting', { default: 'Flipped mounting' })}
						bind:checked={isFlipped}
						size="m"
						id="wizard-flipped"
					/>
				</div>
			</Col>
		</Row>
	</Stack>

	<div class="save-row">
		{#if saved}
			<div class="save-status save-status--ok">
				<i class="fas fa-circle-check" aria-hidden="true"></i>
				{$format('presence_wizard_config_saved', { default: 'Saved — continuing…' })}
			</div>
		{:else if saveError}
			<div class="save-status save-status--error">
				<i class="fas fa-triangle-exclamation" aria-hidden="true"></i>
				{$format('presence_wizard_config_error', {
					default: 'Could not save. Check device connection and try again.',
				})}
			</div>
		{/if}

		{#if !saved}
			<button class="save-btn" class:saving on:click={save} disabled={saving || !device}>
				{#if saving}
					<i class="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
					{$format('presence_wizard_config_saving', { default: 'Saving…' })}
				{:else}
					<i class="fas fa-floppy-disk" aria-hidden="true"></i>
					{$format('presence_wizard_config_save', { default: 'Save to device' })}
				{/if}
			</button>
		{/if}
	</div>
</div>

<style>
	.sensor-step {
		padding: 4px 0;
	}

	.step-subtitle {
		color: var(--color-text-secondary, rgba(255, 255, 255, 0.55));
		line-height: 1.5;
	}

	.field-hint {
		margin: 4px 0 0;
		font-size: 12px;
		color: var(--color-text-muted, rgba(255, 255, 255, 0.4));
		line-height: 1.4;
	}

	.toggle-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		padding: 10px 0;
		border-top: 1px solid var(--color-border, rgba(255, 255, 255, 0.08));
	}

	.toggle-labels {
		flex: 1;
		min-width: 0;
	}

	.toggle-title {
		margin: 0 0 2px;
		font-size: 14px;
		font-weight: 500;
	}

	.save-row {
		margin-top: 24px;
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 12px;
		flex-wrap: wrap;
	}

	.save-btn {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		padding: 8px 18px;
		border-radius: 8px;
		border: none;
		cursor: pointer;
		font-size: 14px;
		font-weight: 600;
		background: var(--color-primary, #7c5bff);
		color: #fff;
		transition: opacity 0.15s;
	}

	.save-btn:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.save-btn.saving {
		opacity: 0.75;
		cursor: default;
	}

	.save-status {
		display: flex;
		align-items: center;
		gap: 6px;
		font-size: 13px;
		font-weight: 500;
	}

	.save-status--ok {
		color: var(--color-success, #00d5b3);
	}

	.save-status--error {
		color: var(--color-danger, #ff4444);
	}
</style>
